import { ArrowDownUp, RotateCcw } from 'lucide-react'

import { FilterBar as Bar } from '@/components/data/FilterBar'
import { SearchInput } from '@/components/data/SearchInput'

export type Filters = {
  q: string
  status: string
  surface: string
  critical: boolean
  silentFailure: boolean
  dryRun: boolean
  since: string // yyyy-mm-dd or ''
  until: string // yyyy-mm-dd or ''
  order: 'desc' | 'asc'
}

export const EMPTY_FILTERS: Filters = {
  q: '',
  status: '',
  surface: '',
  critical: false,
  silentFailure: false,
  dryRun: false,
  since: '',
  until: '',
  order: 'desc',
}

const STATUSES = ['started', 'success', 'partial', 'failed', 'preview', 'awaiting_confirm', 'interrupted', 'unknown']
const SURFACES = ['web', 'mcp', 'cli']

const control =
  'h-7 rounded-md border border-input bg-surface px-2 text-[12px] text-foreground'

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex items-center gap-1.5">
      <span className="text-[11px] text-text-3">{label}</span>
      {children}
    </label>
  )
}

function Flag({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={[
        'h-7 rounded-full border px-2.5 text-[12px] transition-colors',
        active ? 'border-primary bg-surface-active text-foreground' : 'border-line-strong text-text-2 hover:text-foreground',
      ].join(' ')}
    >
      {label}
    </button>
  )
}

/** One line of audit filters: search, status, surface, dates, flags, order, reset. */
export function FilterBar({ value, onChange }: { value: Filters; onChange: (f: Filters) => void }) {
  const set = <K extends keyof Filters>(k: K, v: Filters[K]) => onChange({ ...value, [k]: v })

  const isFiltered = value.q || value.status || value.surface || value.critical || value.silentFailure
    || value.dryRun || value.since || value.until || value.order !== 'desc'

  return (
    <Bar>
      <SearchInput
        value={value.q}
        onChange={(v) => set('q', v)}
        placeholder="Operation or actor…"
        label="Search operation or actor"
        width="w-[200px]"
      />
      <Labeled label="Status">
        <select value={value.status} onChange={(e) => set('status', e.target.value)} className={`${control} w-[124px]`}>
          <option value="">any</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Labeled>
      <Labeled label="Surface">
        <select value={value.surface} onChange={(e) => set('surface', e.target.value)} className={`${control} w-[76px]`}>
          <option value="">any</option>
          {SURFACES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Labeled>
      <Labeled label="Since">
        <input type="date" value={value.since} onChange={(e) => set('since', e.target.value)} className={`${control} w-[124px]`} />
      </Labeled>
      <Labeled label="Until">
        <input type="date" value={value.until} onChange={(e) => set('until', e.target.value)} className={`${control} w-[124px]`} />
      </Labeled>
      <span className="flex items-center gap-1.5">
        <Flag label="critical" active={value.critical} onClick={() => set('critical', !value.critical)} />
        <Flag label="silent failure" active={value.silentFailure} onClick={() => set('silentFailure', !value.silentFailure)} />
        <Flag label="dry run" active={value.dryRun} onClick={() => set('dryRun', !value.dryRun)} />
      </span>
      <span className="ml-auto flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => set('order', value.order === 'desc' ? 'asc' : 'desc')}
          className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-[12px] text-text-2 transition-colors hover:bg-surface-raised hover:text-foreground"
          title="Toggle sort order"
        >
          <ArrowDownUp aria-hidden className="size-3.5" />
          {value.order === 'desc' ? 'Newest first' : 'Oldest first'}
        </button>
        {isFiltered && (
          <button
            type="button"
            onClick={() => onChange(EMPTY_FILTERS)}
            className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-[12px] text-text-2 transition-colors hover:bg-surface-raised hover:text-foreground"
            title="Reset all filters"
          >
            <RotateCcw aria-hidden className="size-3.5" />
            Reset
          </button>
        )}
      </span>
    </Bar>
  )
}
